'use client';

import { AtomicContext } from '@contexts/coveo/AtomicContext';
import {
  SearchResultsBannerDisplayType,
  SearchResultsBannerDisplayTypePriority,
  SearchResultsBanner as SearchResultsBannerType,
} from '@lib/types/search/SearchResultsBanner';
import c from 'classnames';
import Image from 'next/image';
import { useContext } from 'react';
import { Link } from 'ui-components';
import { useResults } from './hooks/useResults';
import { useUrlManager } from './hooks/useUrlManager';

type SearchResultsBannerProps = {
  banners: SearchResultsBannerType[];
  context: AtomicContext;
};

const isBannerDisplayable = (
  banner: SearchResultsBannerType,
  filtersString: string | null
) => {
  if (banner.displayType === SearchResultsBannerDisplayType.FLASH_DEAL) {
    return filtersString?.includes('flash_deal');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.NEW_RELEASE) {
    return filtersString?.includes('new_release_discount');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.ON_SALE) {
    return filtersString?.includes('on_sale');
  }

  return banner.displayType === SearchResultsBannerDisplayType.DEFAULT;
};

/**
 * Sale banners will show if their respective sale filter is active, if multiple filters and banners are active, the priority will be used to determine which banner to show.
 * Default banners will always be shown, but if a sale banner is displayable, it will take priority so only 1 banner is displayed at a time.
 */
const useGetActiveBanner = (
  banners: SearchResultsBannerType[],
  filtersString: string | null
) => {
  const displayableBanners = banners
    .filter((banner) => isBannerDisplayable(banner, filtersString))
    .sort((a, b) => {
      return (
        SearchResultsBannerDisplayTypePriority[a.displayType] -
        SearchResultsBannerDisplayTypePriority[b.displayType]
      );
    });

  return displayableBanners[0];
};

export const SearchResultsBanner = ({
  banners,
  context,
}: SearchResultsBannerProps) => {
  const { searchEngine } = useContext(context);
  const { urlManagerState } = useUrlManager(context);
  const urlParams = new URLSearchParams(urlManagerState?.fragment);
  const activeBanner = useGetActiveBanner(
    banners,
    urlParams?.get('f-ec_sale_filters')
  );
  const { results: resultState } = useResults(searchEngine);
  const hasResults = resultState?.results && resultState.results.length > 0;

  if (!activeBanner || !hasResults) {
    return null;
  }

  return (
    <Link
      href={activeBanner.link.href}
      target={activeBanner.link.openInNewTab ? '_blank' : undefined}
      className={c('relative mb-6 block w-full md:aspect-[32/5] lg:mb-14', {
        ['aspect-[32/5]']: !activeBanner.mobileImage,
        ['aspect-[49/16]']: activeBanner.mobileImage,
      })}
    >
      <Image
        fill
        sizes="100vw"
        src={activeBanner.image.url}
        alt={activeBanner.image.alt ?? ''}
        className={c('object-cover', {
          ['hidden md:block']: activeBanner.mobileImage,
        })}
      />
      {activeBanner.mobileImage && (
        <Image
          fill
          sizes="100vw, (min-width: 768px) 0vw"
          src={activeBanner.mobileImage.url}
          alt={activeBanner.mobileImage.alt ?? ''}
          className={c('block object-cover md:hidden')}
        />
      )}
    </Link>
  );
};
